import { MAILBOX_MIN_QUOTA_BYTES, OPERATIONS_MAILBOX_LIMIT_BYTES } from '@bekebi/domain';

/**
 * Mailbox sizes for people. Decimal units, as the Mail Server's own ("1.0 GB" = 1,000,000,000
 * bytes). The quota rules (DECISIONS.md A-K15) are repeated here only to guide the form; the API
 * enforces them on every request and is the authority.
 */
export const GB = 1_000_000_000;

const UNITS = ['KB', 'MB', 'GB', 'TB', 'PB'] as const;

/** e.g. 0 → "0 bytes", 269_600_000 → "269.6 MB", 1_000_000_000 → "1.0 GB". */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} ${bytes === 1 ? 'byte' : 'bytes'}`;
  let value = bytes;
  let unit = -1;
  while (value >= 1000 && unit < UNITS.length - 1) {
    value /= 1000;
    unit++;
  }
  return `${value.toFixed(1)} ${UNITS[unit]}`;
}

/** A mailbox's size limit for people: null (the Mail Server sets no limit) → "Unlimited". */
export function formatQuota(quotaBytes: number | null): string {
  return quotaBytes === null ? 'Unlimited' : formatBytes(quotaBytes);
}

/** A quota as a GB figure for an input field: 1_100_000_000 → "1.1". */
export function bytesToGbText(bytes: number): string {
  return String(Number((bytes / GB).toFixed(3)));
}

/** "2.5" → 2_500_000_000; null when the text is not a number. */
export function gbTextToBytes(text: string): number | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  return Math.round(Number(trimmed) * GB);
}

/**
 * Why a requested size cannot be used, or null. `unlimited`: the session may assign more than
 * 5 GB (the Admin's `mail.mailboxes.manage_unlimited`).
 */
export function quotaProblem(bytes: number | null, unlimited: boolean): string | null {
  if (bytes === null) return 'Enter a size in GB, for example 1 or 2.5.';
  if (bytes < MAILBOX_MIN_QUOTA_BYTES) return 'Mailbox sizes start at 1 GB.';
  if (!unlimited && bytes > OPERATIONS_MAILBOX_LIMIT_BYTES) {
    return 'Mailbox sizes above 5 GB need the Admin.';
  }
  if (bytes > Number.MAX_SAFE_INTEGER) return 'That size is too large.';
  return null;
}

/** The size hint shown next to a size field. */
export function quotaHint(unlimited: boolean): string {
  return unlimited
    ? 'At least 1 GB. Above 5 GB, check the Mail Server’s available capacity first.'
    : 'From 1 GB to 5 GB. Larger mailboxes are handled by the Admin.';
}

/**
 * May this session change this mailbox's size at all? ICT cannot touch one above 5 GB, or one
 * without any limit (OD-1).
 */
export function canChangeSize(quotaBytes: number | null, unlimited: boolean): boolean {
  return unlimited || (quotaBytes !== null && quotaBytes <= OPERATIONS_MAILBOX_LIMIT_BYTES);
}
